import * as Sentry from '@sentry/angular';
import type { ErrorEvent } from '@sentry/angular';
import { HttpErrorResponse } from '@angular/common/http';

const HTTP_401_METRIC = 'http.response.401';

export type Http401MetricContext =
  | 'refresh_eligible'
  | 'logout'
  | 'auth_screen'
  | 'public_endpoint'
  | 'other';

/** Drop expected 401 noise from Sentry Issues while still counting them as metrics. */
export function isUnauthorizedSentryEvent(event: ErrorEvent): boolean {
  const responseStatus = event.contexts?.response?.status_code;
  if (responseStatus === 401) {
    return true;
  }

  const message = event.exception?.values?.[0]?.value ?? event.message ?? '';
  if (/Http failure response.*\b401\b/i.test(message)) {
    return true;
  }
  if (/\b401\b.*Unauthorized/i.test(message)) {
    return true;
  }

  const extra = event.extra;
  if (extra && typeof extra === 'object' && 'status' in extra) {
    const status = (extra as { status?: unknown }).status;
    if (status === 401 || status === '401') {
      return true;
    }
  }

  return false;
}

/** Status 0 = browser/network/CORS/cancelled — not an actionable app exception. */
function getSentryEventDiagnosticText(event: ErrorEvent): string {
  const chunks: string[] = [];
  if (typeof event.message === 'string') {
    chunks.push(event.message);
  }
  for (const entry of event.exception?.values ?? []) {
    if (typeof entry?.value === 'string') {
      chunks.push(entry.value);
    }
  }
  return chunks.join('\n');
}

export function isBenignHttpFailureSentryEvent(event: ErrorEvent): boolean {
  const responseStatus = event.contexts?.response?.status_code;
  if (responseStatus === 0) {
    return true;
  }

  const message = getSentryEventDiagnosticText(event);
  if (/Http failure response for .+?:\s*0(?:\s+Unknown Error)?/i.test(message)) {
    return true;
  }
  // Zone/Angular wraps: "Uncaught (in promise): Http failure response for ...: 0 Unknown Error"
  if (/Uncaught \(in promise\).*Http failure response.*:\s*0\b/i.test(message)) {
    return true;
  }
  // Asset translation loads aborted on navigation / second-screen close
  if (/assets\/json\/translations-/i.test(message) && /Unknown Error/i.test(message)) {
    return true;
  }

  const extra = event.extra;
  if (extra && typeof extra === 'object' && 'status' in extra) {
    const status = (extra as { status?: unknown }).status;
    if (status === 0 || status === '0') {
      return true;
    }
  }

  return false;
}

export function isExtensionAsyncChannelMessage(message: string): boolean {
  if (!message) {
    return false;
  }
  // Very specific to Chrome extension messaging; safe to match on the tail phrase alone.
  if (/message channel closed before a response was received/i.test(message)) {
    return true;
  }
  return (
    /a listener indicated an asynchronous response by returning true/i.test(message) &&
    /message channel closed before a response was received/i.test(message)
  );
}

export function extractErrorMessage(reason: unknown): string {
  if (typeof reason === 'string') {
    return reason;
  }
  if (reason instanceof Error) {
    return reason.message;
  }
  if (reason && typeof reason === 'object' && 'message' in reason) {
    return String((reason as { message?: unknown }).message ?? '');
  }
  return '';
}

/** Chrome extension messaging noise — not an application defect. */
export function isExtensionAsyncChannelSentryEvent(event: ErrorEvent): boolean {
  const text = getSentryEventDiagnosticText(event);
  return isExtensionAsyncChannelMessage(text);
}

/** pdf.js race when cleanup runs while a page is still rendering — not an app defect. */
export function isBenignPdfJsCleanupMessage(message: string): boolean {
  return (
    /startCleanup:\s*Page\s+\d+\s+is currently rendering/i.test(message) ||
    /Uncaught \(in promise\).*startCleanup:\s*Page\s+\d+\s+is currently rendering/i.test(message) ||
    (/currently rendering/i.test(message) && /startCleanup/i.test(message))
  );
}

export function isBenignPdfJsCleanupSentryEvent(event: ErrorEvent): boolean {
  return isBenignPdfJsCleanupMessage(getSentryEventDiagnosticText(event));
}

export function shouldDropSentryHttpNoiseEvent(event: ErrorEvent): boolean {
  return (
    isUnauthorizedSentryEvent(event) ||
    isBenignHttpFailureSentryEvent(event) ||
    isExtensionAsyncChannelSentryEvent(event) ||
    isBenignPdfJsCleanupSentryEvent(event)
  );
}

export function recordHttp401Metric(
  error: HttpErrorResponse,
  requestUrl: string,
  context: Http401MetricContext,
): void {
  const client = Sentry.getClient();
  if (!client?.getOptions().enabled || error.status !== 401) {
    return;
  }

  Sentry.metrics.count(HTTP_401_METRIC, 1, {
    attributes: {
      endpoint: sanitizeUrlForMetric(requestUrl),
      context,
    },
  });
}

function sanitizeUrlForMetric(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0];
  }
}
